/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface PDFViewerProps {
    filePath: string;
    fileName?: string;
    dataTestSelector?: string;
    asLink?: boolean;
    newTab?: boolean;
    extraQueryStringParams?: any;
    extendedStyles?: PDFViewerStyles;
}

type Props = PDFViewerProps & HasHistory;

export interface PDFViewerStyles {
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    iframeWrapper?: InjectableCss;
}

const pDFViewerStyles: PDFViewerStyles = {
    documentLink: {
        css: css`
            @media print {
                display: block !important;
            }
        `,
        typographyProps: {
            css: css`
                display: flex;
                align-items: center;
                line-height: 20px;
                color: ${getColor("text.main")} !important;

                &:hover,
                &:focus {
                    text-decoration: underline;
                    color: ${getColor("primary.main")} !important;
                }
            `,
        },
    },
    documentIcon: {
        src: File,
        color: "text.main",
        css: css`
            margin-right: 5px;
        `,
    },
    iframeWrapper: {
        css: css`
            margin-top: 30px;
            height: 700px;

            embed {
                width: 100%;
                height: 100%;
            }
        `,
    },
};

const style = pDFViewerStyles;

const PDFViewer: React.FC<Props> = ({
    filePath,
    fileName,
    dataTestSelector,
    asLink,
    newTab,
    extraQueryStringParams,
    extendedStyles,
    history,
}) => {
    if (!filePath || !fileName) {
        return null;
    }

    const styles = mergeToNew(style, extendedStyles);

    const handleClick = (e: any) => {
        e.preventDefault();

        const params = {
            fileName,
            filePath,
            ...extraQueryStringParams,
        };

        const queryString = Object.keys(params)
            .map((key: any) => `${key}=${params[key]}`)
            .join("&");

        history.push(`${window.location.pathname}?${queryString}`);
    };

    if (asLink) {
        const props = {
            ...styles.documentLink,
            "data-test-selector": dataTestSelector,
        } as any;

        if (newTab) {
            props.href = filePath;
            props.target = "_blank";
        } else {
            props.onClick = handleClick;
        }

        return (
            <StyledWrapper>
                <Link {...props}>
                    <IconMemo {...styles.documentIcon} />
                    <Typography {...styles.documentNameText}>{fileName || "File Name"}</Typography>
                </Link>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.iframeWrapper}>
            <embed src={filePath} />
        </StyledWrapper>
    );
};

export default connect()(withHistory(PDFViewer));
